import { parseISO } from 'date-fns';
import { Utils } from 'klayowebshared';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { Utils as CommonUtils } from '../../common/Utils';
import { IChecklists } from '../../interfaces/CheckLists';
import { SELECTED_FILE_TYPE } from '../../interfaces/Practical';
import { DataContext } from '../../types/data.types';
import { AbstractAttribute } from '../attributes/AbstractAttribute';
import { AttributeRequestActivity } from '../attributes/AttributeRequestActivity';
import { DocumentList } from '../documents/DocumentList';
import { SettingsCourse, SettingsCourseData } from './SettingCourse';
import {
  SettingsAttributeCategory,
  SettingsAttributeCategoryData,
} from './SettingsAttributeCategory';

// Notification period type
export interface NotificationPeriod {
  index: number;
  label: string;
}

// Validation request state type
export interface ValidationRequestState {
  index: number;
  label?: string;
}

// Attribute requirement type
export interface AttributeRequirementType {
  index: number;
  label: string;
}

// Validation request data
export interface ValidationRequest {
  proficiencyValue?: number;
  expiryDate?: string;
}

// API data for SettingsAttribute
export interface SettingsAttributeData {
  attributeDefinitionChildId?: string;
  attributeDefinitionId?: string;
  attributeDefinitionName?: string;
  attributeDefinitionDescription?: string;
  name?: string;
  description?: string;
  isParent?: boolean;
  practicalTrainingHours?: number;
  numberOfEmployeeAttributes?: number;
  numberOfJobAttributes?: number;
  numberOfRegulationTasks?: number;
  employeeAttributeId?: string;
  hasTrainingCourses?: boolean;
  supportingEvidenceUploadIds?: string[];
  checklistAnswers?: IChecklists[];
  deletedChecklistAssignmentIds?: string[];
  fileType?: SELECTED_FILE_TYPE;
  files?: File[];
  checklists?: IChecklists[];
  attributeCategories?: SettingsAttributeCategoryData[];
  attributeNotifications?: { attributeNotificationType: number }[];
  requirementType?: number | AttributeRequirementType;
  trainingCourses?: SettingsCourseData[];
  /** OLC catalogue links; a separate collection because the id spaces are unrelated. */
  catalogueCourses?: SettingsCourseData[];
  proficiencyMinimum?: number;
  proficiencyValue?: number;
  proficiency?: number;
  comment?: string;
  expiryDate?: string;
  validationRequest?: ValidationRequest;
  requestedProficiency?: number;
  requestedExpiryDate?: Date;
  pendingExpiryDate?: string;
  pendingProficiency?: number;
  attributeDefinitionChildren?: SettingsAttributeData[];
  jobAttributeChildren?: SettingsAttributeData[];
  attributeDefinitionAttributeDefinitionChildren?: SettingsAttributeData[];
  mandatory?: boolean;
  required?: boolean;
  hasDocuments?: boolean;
  importCode?: string;
  code?: string;
  hasExpiryDate?: boolean;
  attributeDefinitionHasExpiryDate?: boolean;
  hasProficiency?: boolean;
  attributeDefinitionHasProficiency?: boolean;
  validationRequestState?: number | ValidationRequestState;
  originalValidationRequestState?: ValidationRequestState;
  requestActivity?: AttributeRequestActivity;
  documents?: { list: unknown[] };
  isNew?: boolean;
  isMinimum?: boolean;
}

export class SettingsAttribute {
  // Collection properties - initialized with empty arrays
  categories: SettingsAttributeCategory[] = [];
  trainingCourses: SettingsCourse[] = [];
  trainingCourseIds: string[] = [];
  childAttributes: SettingsAttribute[] = [];
  expiryNotifications: NotificationPeriod[] = [];
  files: File[] = [];
  supportingEvidenceUploadIds: string[] = [];
  checklistAnswers: IChecklists[] = [];
  checklists: IChecklists[] = [];
  deletedChecklistAssignmentIds: string[] = [];
  attributeHierarchy: string[] = [];

  // ID properties
  attributeId: string | undefined;
  attributeDefinitionId: string | undefined;
  rowId: string | undefined;
  parentAttributeId: string | undefined;
  id: string | undefined;
  employeeAttributeId: string | undefined;

  // String properties
  name: string | undefined;
  description: string | undefined;
  type: string | undefined;
  importCode: string | undefined;
  comment: string | undefined;
  needsFeedbackComment: string | undefined;

  // Number properties
  defaultProficiencyMinimum: number | undefined;
  proficiencyMinimum: number | undefined;
  proficiencyValue: number | undefined;
  currentProficiency: number | undefined;
  proficiency: number | undefined;
  requestedProficiency: number | undefined;
  pendingProficiency: number | undefined;
  childIndex: number | undefined;
  numberOfEmployeeAttributes = 0;
  numberOfJobAttributes = 0;
  numberOfRegulationTasks: number | undefined;
  practicalTrainingHours = 0;

  // Boolean properties - initialized with defaults
  isParent = false;
  isChild = false;
  isLastChild = false;
  hasDocuments = false;
  isNew = false;
  needsFeedback = false;
  isJob = false;
  isInUsed = false;
  isTasked = false;
  hasTrainingCourses = false;
  hasExpiryDate = false;
  hasProficiency = false;
  isMinimum: boolean | undefined;

  // Date properties
  currentExpiryDate: Date | null | undefined;
  expiryDate: Date | null | undefined;
  requestedExpiryDate: Date | null | undefined;
  pendingExpiryDate: Date | null | undefined | string;

  // Complex type properties
  requirementType: AttributeRequirementType | number | undefined;
  validationRequest: ValidationRequest = {};
  validationRequestState: ValidationRequestState | undefined;
  originalValidationRequestState: ValidationRequestState | undefined;
  requestActivity: AttributeRequestActivity | undefined;
  documents: DocumentList | undefined;
  fileType: SELECTED_FILE_TYPE = SELECTED_FILE_TYPE.DOCUMENT;

  [key: string]: unknown;

  static notificationPeriods = {
    none: {
      index: 0,
      label: 'None',
    },
    onExpiry: {
      //OnExpiry
      index: 6,
      label: 'On expiry',
    },
    week: {
      //OneWeekBefore
      index: 1,
      label: '1 week before',
    },
    weeks2: {
      //TwoWeekBefore
      index: 2,
      label: '2 weeks before',
    },
    weeks3: {
      //ThreeWeekBefore
      index: 3,
      label: '3 weeks before',
    },
    month: {
      //OneMonthBefore
      index: 4,
      label: '1 month before',
    },
    months2: {
      //TwoMonthBefore
      index: 5,
      label: '2 months before',
    },
    months3: {
      //ThreeMonthBefore
      index: 7,
      label: '3 months before',
    },
  };

  constructor(data?: SettingsAttributeData) {
    // All defaults are now set via class field initializers above
    // Only process if data is provided
    if (data) {
      //Is child? - diff names, no children
      if (data.attributeDefinitionChildId) {
        this.attributeId = data.attributeDefinitionId;
        this.attributeDefinitionId = data.attributeDefinitionId;
        this.name = data.attributeDefinitionName;
        this.description = data.attributeDefinitionDescription;
        this.isParent = false;
      } else {
        this.attributeId = data.attributeDefinitionId;
        this.attributeDefinitionId = data.attributeDefinitionId;
        this.name = data.name || data.attributeDefinitionName;
        this.description = data.description;
        this.isParent = data.isParent || false;
        this.practicalTrainingHours = data.practicalTrainingHours || 0;
        this.numberOfEmployeeAttributes = data.numberOfEmployeeAttributes || 0;
        this.numberOfJobAttributes = data.numberOfJobAttributes || 0;
        this.isJob = (data.numberOfJobAttributes || 0) !== 0;
        this.isInUsed = (data.numberOfEmployeeAttributes || 0) !== 0;
        this.isTasked = (data.numberOfRegulationTasks || 0) !== 0;
        this.employeeAttributeId = data.employeeAttributeId;
        this.hasTrainingCourses = data.hasTrainingCourses || false;
        this.supportingEvidenceUploadIds = data.supportingEvidenceUploadIds || [];
        this.checklistAnswers = data.checklistAnswers || [];
        this.deletedChecklistAssignmentIds = data.deletedChecklistAssignmentIds || [];

        this.fileType = data.fileType || SELECTED_FILE_TYPE.DOCUMENT;

        if (
          data.files &&
          Object.entries(data.files).filter(([, value]) => Boolean(value)).length > 0
        ) {
          this.files = Object.values(data.files).filter((f): f is File => f !== null);
        }

        if (!isEmpty(data.checklists)) {
          this.checklists = data.checklists
            ? data.checklists.map((c: IChecklists) => ({
                ...c,
                answers: CommonUtils.getAnswersFromChecklist(c),
              }))
            : [];
        }

        if (data.attributeCategories) {
          data.attributeCategories.forEach((cat) =>
            this.categories.push(new SettingsAttributeCategory(cat))
          );
        }

        if (data.attributeNotifications) {
          data.attributeNotifications.forEach((e) => {
            const notification = Object.values(SettingsAttribute.notificationPeriods).find(
              (n) => n.index === e.attributeNotificationType
            );
            if (notification) {
              this.expiryNotifications.push(notification);
            }
          });
        }

        if (data.requirementType !== null && data.requirementType !== undefined) {
          if (typeof data.requirementType === 'object') {
            this.requirementType = data.requirementType;
          } else {
            this.requirementType =
              (Object.values(AbstractAttribute.type).find(
                (t) => (t as AttributeRequirementType).index === data.requirementType
              ) as AttributeRequirementType) || undefined;
          }
        }

        if (data.trainingCourses) {
          data.trainingCourses.forEach((course) =>
            this.trainingCourses.push(new SettingsCourse(course))
          );
          data.trainingCourses.forEach((course) => {
            if (course.id) this.trainingCourseIds.push(course.id);
          });
        }

        // Catalogue (OLC) links arrive in their own collection and are shown in the same picker.
        // Without this the editor cannot see them, so it cannot echo them back on save — and because
        // each field is the COMPLETE desired set for its side, an unseen link is a deleted link.
        if (data.catalogueCourses) {
          data.catalogueCourses.forEach((course) => {
            const catalogueCourse = new SettingsCourse(course);
            catalogueCourse.isCatalogue = true;
            this.trainingCourses.push(catalogueCourse);
          });
        }

        this.proficiencyMinimum = data.proficiencyMinimum || this.defaultProficiencyMinimum;
        this.proficiencyValue =
          data.proficiencyValue !== undefined && !isNaN(data.proficiencyValue)
            ? data.proficiencyValue
            : data.proficiency;
        this.comment = data.comment;

        this.currentProficiency = data.proficiencyValue;

        this.currentExpiryDate = data.expiryDate ? new Date(parseISO(data.expiryDate)) : undefined;

        this.proficiency = this.proficiencyValue ? this.proficiencyValue : this.proficiency;
        this.expiryDate = this.currentExpiryDate;
        this.validationRequest = data.validationRequest || {};

        this.requestedProficiency = data.validationRequest
          ? data.validationRequest.proficiencyValue
          : data.requestedProficiency;
        this.requestedExpiryDate = data.validationRequest?.expiryDate
          ? new Date(parseISO(data.validationRequest.expiryDate || ''))
          : data.requestedExpiryDate;

        this.pendingExpiryDate = data.pendingExpiryDate
          ? data.pendingExpiryDate
          : this.requestedExpiryDate || this.currentExpiryDate;

        this.pendingProficiency = data.pendingProficiency
          ? data.pendingProficiency
          : this.requestedProficiency || this.currentProficiency;

        const childAttrs =
          data.attributeDefinitionChildren ||
          data.jobAttributeChildren ||
          data.attributeDefinitionAttributeDefinitionChildren;
        if (childAttrs && childAttrs.length !== 0) {
          this.isParent = true;
          this.requirementType = undefined;

          childAttrs.forEach((c, index) => {
            const childAttr = new SettingsAttribute(c);
            childAttr.rowId = this.attributeDefinitionId! + childAttr.attributeDefinitionId;
            childAttr.isChild = true;
            childAttr.childIndex = index;
            childAttr.isLastChild = index === childAttrs.length - 1;
            childAttr.attributeHierarchy = [
              this.attributeDefinitionId!,
              childAttr.attributeDefinitionId!,
            ];
            childAttr.parentAttributeId = this.attributeId;
            this.childAttributes.push(childAttr);
          });
        }

        if (data.mandatory) this.type = AbstractAttribute.type.mandatory.translationKey;
        else if (data.required) this.type = AbstractAttribute.type.required.translationKey;
        else this.type = AbstractAttribute.type.desirable.translationKey;
      }

      this.id = this.attributeId;
      this.rowId = this.attributeId;
      this.attributeHierarchy = [this.attributeDefinitionId!];
      this.hasDocuments = data.hasDocuments || false;
      this.importCode = data.importCode ?? data.code;
      this.hasExpiryDate = data.hasExpiryDate ?? data.attributeDefinitionHasExpiryDate ?? false;
      this.hasProficiency = data.hasProficiency ?? data.attributeDefinitionHasProficiency ?? false;

      if (data.validationRequestState !== undefined) {
        this.validationRequestState =
          typeof data.validationRequestState === 'number'
            ? (Object.values(AbstractAttribute.status)[
                data.validationRequestState
              ] as ValidationRequestState)
            : data.validationRequestState;
      }

      this.originalValidationRequestState =
        data.originalValidationRequestState || this.validationRequestState;

      if (data.requestActivity) this.requestActivity = data.requestActivity;
      if (data.documents) this.documents = new DocumentList(data.documents.list);
      if (
        data.files &&
        Object.entries(data.files).filter(([, value]) => Boolean(value)).length > 0
      ) {
        this.files = Object.values(data.files).filter((f): f is File => f !== null);
      }

      this.isNew = data.isNew || false;
      this.isMinimum = data.isMinimum;
      this.supportingEvidenceUploadIds = data.supportingEvidenceUploadIds || [];
      this.checklistAnswers = data.checklistAnswers || [];
      this.deletedChecklistAssignmentIds = data.deletedChecklistAssignmentIds || [];

      if (!isEmpty(data.checklists)) {
        this.checklists = data.checklists
          ? data.checklists.map((c: IChecklists) => ({
              ...c,
              answers: CommonUtils.getAnswersFromChecklist(c),
            }))
          : [];
      }

      this.fileType = data.fileType || SELECTED_FILE_TYPE.DOCUMENT;

      this.updateNeedsFeedback();
    }
    // No else needed - all defaults set via class field initializers
  }

  updateNeedsFeedback() {
    const feedbackState =
      this.validationRequestState &&
      this.validationRequestState === AbstractAttribute.status.feedback;
    if (feedbackState) this.needsFeedbackComment = '';

    this.needsFeedback =
      (this.hasProficiency === true && !this.proficiencyValue) ||
      this.hasExpiryDate === true ||
      (this.comment && this.comment !== '') ||
      feedbackState === true;

    if (this.needsFeedback) {
      if (this.hasProficiency === true && this.hasExpiryDate === true)
        this.needsFeedbackComment =
          'This attribute needs Expiry Date and Proficiency set. Please update and submit.';
      else if (this.hasProficiency === true)
        this.needsFeedbackComment =
          'This attribute needs Proficiency set. Please update and submit.';
      else if (this.hasExpiryDate === true)
        this.needsFeedbackComment =
          'This attribute needs Expiry Date set. Please update and submit.';
    }
  }

  hasValidationRequest() {
    return this.validationRequestState !== undefined;
  }

  hasValidExpiryDate() {
    return (
      this.expiryDate !== undefined && this.expiryDate !== null && this.expiryDate >= new Date()
    );
  }

  hasValidProficiency() {
    return (
      this.proficiency !== undefined &&
      this.proficiency !== null &&
      !isNaN(this.proficiency) &&
      this.proficiency !== 0
    );
  }

  hasValidPendingExpiryDate() {
    return this.pendingExpiryDate !== undefined && this.pendingExpiryDate !== null;
    //return this.pendingExpiryDate !== undefined && this.pendingExpiryDate !== null && this.pendingExpiryDate >= new Date();
  }

  hasValidPendingProficiency() {
    return (
      this.pendingProficiency !== undefined &&
      this.pendingProficiency !== null &&
      !isNaN(this.pendingProficiency) &&
      this.pendingProficiency !== 0
    );
  }

  toStandardAttribute() {
    const stdAttr = new SettingsAttribute(this as unknown as SettingsAttributeData);
    stdAttr.attributeHierarchy = [stdAttr.attributeDefinitionId || ''];
    stdAttr.rowId = stdAttr.attributeDefinitionId || '';
    return stdAttr;
  }

  loadRequestActivity(
    context: DataContext,
    updateCache?: boolean
  ): Promise<AttributeRequestActivity> {
    return new Promise((resolve, reject) => {
      if (!this.requestActivity || updateCache === true) {
        this.requestActivity = new AttributeRequestActivity({
          attributeId: this.employeeAttributeId,
          resolve: (requestActivity: AttributeRequestActivity) => {
            this.documents = requestActivity.documents;
            resolve(requestActivity);
          },
          reject,
          context,
        });
      } else resolve(this.requestActivity);
    });
  }

  compareCategories(other: SettingsAttribute): boolean {
    return Utils.compareList(
      this.categories,
      other.categories,
      (m1: SettingsAttributeCategory, m2: SettingsAttributeCategory) =>
        m1.categoryId === m2.categoryId
    );
  }

  deleteDocument(doc: { isDeleted?: boolean; pendingDelete?: boolean }): void {
    if (!this.documents) return;

    this.documents.delete(doc);
    this.hasDocuments = this.documents.list.some(
      (d: { isDeleted?: boolean; pendingDelete?: boolean }) => !d.isDeleted && !d.pendingDelete
    );
  }

  deleteChecklistAssignment(checklistAssignmentId: string) {
    if (!this.checklists) return;

    this.checklists = this.checklists.filter((c) => c.assignmentId !== checklistAssignmentId);
    this.deletedChecklistAssignmentIds.push(checklistAssignmentId);
  }

  isSame(other: SettingsAttribute): boolean {
    if (!other) return false;

    if (this.validationRequestState && other.validationRequestState) {
      if (this.validationRequestState.index !== other.validationRequestState.index) {
        return false;
      }
    }

    if (this.hasProficiency && other.hasProficiency) {
      if (this.pendingProficiency !== other.pendingProficiency) {
        return false;
      }
    }

    if (this.hasExpiryDate && other.hasExpiryDate) {
      if (
        this.pendingExpiryDate &&
        other.pendingExpiryDate &&
        Utils.formatReadableDate(this.pendingExpiryDate) !==
          Utils.formatReadableDate(other.pendingExpiryDate)
      ) {
        return false;
      }
    }

    if (
      !(!this.comment && other.comment === '') &&
      !(this.comment === '' && !other.comment) &&
      this.comment !== other.comment
    ) {
      return false;
    }

    //Only compare if feed is loaded
    if (this.requestActivity && other.requestActivity) {
      if (JSON.stringify(this.documents) !== JSON.stringify(other.documents)) {
        return false;
      }
    }

    if (this.files || other.files) {
      const thisFileNames = SettingsAttribute.getFileNames(this.files);
      const otherFileNames = SettingsAttribute.getFileNames(other.files);

      if (!isEqual(thisFileNames, otherFileNames)) {
        return false;
      }
    }

    if (!isEqual(this.checklists, other.checklists)) {
      return false;
    }

    if (!isEqual(this.checklistAnswers, other.checklistAnswers)) {
      return false;
    }

    return true;
  }

  private static getFileNames(files: File[]): string[] {
    if (!files) return [];

    return files.map((file: File) => file.name);
  }

  toApiDto(): {
    attributeDefinitionId: string | undefined;
    name: string | undefined;
    description: string | undefined;
    attributeCategoryIds: (string | undefined)[];
    attributeDefinitionChildren: (string | undefined)[];
    hasExpiryDate: boolean;
    hasProficiency: boolean;
    trainingCourseIds: (string | undefined)[];
    catalogueCourseIds: (string | undefined)[];
    code: string | undefined;
    attributeNotificationTypes: number[] | null;
  } {
    const validExpiryNotifs =
      this.expiryNotifications && this.expiryNotifications.length !== 0
        ? Object.values(this.expiryNotifications)
        : null;
    const isParent = this.childAttributes && this.childAttributes.length !== 0;

    return {
      attributeDefinitionId: this.attributeId,
      name: this.name,
      description: this.description,
      attributeCategoryIds:
        this.categories && this.categories.length !== 0
          ? this.categories.map((cat: SettingsAttributeCategory) => cat.categoryId)
          : [],
      attributeDefinitionChildren:
        this.childAttributes && this.childAttributes.length !== 0
          ? this.childAttributes.map((attr: SettingsAttribute) => attr.attributeId)
          : [],
      hasExpiryDate: !isParent && this.hasExpiryDate,
      hasProficiency: !isParent && this.hasProficiency,
      // Split by provider: catalogue ids resolve against dbo.Course, integration ids against
      // IntegrationCourse. Sending either through the other field is how a selection used to vanish
      // on save (update) or hit a foreign-key violation (add).
      trainingCourseIds:
        this.trainingCourses && this.trainingCourses.length !== 0
          ? this.trainingCourses
              .filter((course: SettingsCourse) => !course.isCatalogue)
              .map((course: SettingsCourse) => course.id)
          : [],
      catalogueCourseIds:
        this.trainingCourses && this.trainingCourses.length !== 0
          ? this.trainingCourses
              .filter((course: SettingsCourse) => course.isCatalogue)
              .map((course: SettingsCourse) => course.id)
          : [],
      code: this.importCode,
      attributeNotificationTypes:
        !isParent && this.hasExpiryDate
          ? validExpiryNotifs !== null && validExpiryNotifs.length !== 0
            ? validExpiryNotifs.map((e: NotificationPeriod) => e.index)
            : [SettingsAttribute.notificationPeriods.none.index]
          : null,
    };
  }
}
